/* ----------------- React --------------- */
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
} from "react";

/* ----------------- Third Party Libraries --------------- */
import {
	BottomSheetBackdrop,
	BottomSheetModal,
	BottomSheetModalProvider,
} from "@gorhom/bottom-sheet";

import AudioPlayerContainer from "@features/session/containers/AudioPlayerContainer";
/* ----------------- Hooks --------------- */
import { useAppTheme } from "@hooks/useAppTheme";
import type {
	Professor,
	SkillTypes,
	Slide,
} from "@memorang/types/src/session/new-session";
import { Box, Button, SheetHandle, VideoPlayer } from "@memorang/ui";
import InfoAlert from "components/InfoAlert";
import { useTranslation } from "react-i18next";
import type {
	NativeScrollEvent,
	NativeSyntheticEvent,
	Text as TextRef,
} from "react-native";
import { ScrollView } from "react-native";
import { Divider, Text } from "react-native-paper";
import DiscussionView from "../DiscussionView";
import ItemStem from "../ItemStem";
import SlideShowRenderer from "../SlideShowRenderer";
import type { SlideData } from "../Slideshow";
import StimulusInfo from "../StimulusInfo";
import StimulusTabContent from "../StimulusTabContent";

type Props = {
	showModal?: boolean;
	toggleShowModal: () => void;
	parentStem?: string;
	parentInstruction?: string;
	parentAudio?: string;
	parentVideo?: string;
	parentTitle?: string;
	professor?: Professor;
	handleClickInsertableText?: (choiceId: string) => void;
	mode?: "sheet" | "modal";
	parentIndex?: number;
	slideshowData?: SlideData[];
	skill?: SkillTypes;
	isSlidesHasPassage?: boolean;
	slides?: Slide[];
	insertText?: string | null;
	disableInsertableText?: boolean;
	itemId?: number;
};

const PassageStemModal = forwardRef(
	(
		{
			showModal,
			toggleShowModal,
			parentStem,
			parentInstruction,
			parentTitle,
			handleClickInsertableText,
			mode = "sheet",
			parentIndex,
			slideshowData,
			professor,
			skill,
			slides,
			isSlidesHasPassage,
			insertText,
			disableInsertableText,
			itemId,
			parentVideo,
			parentAudio,
		}: Props,
		ref,
	) => {
		const { t } = useTranslation();
		const snapPoints = useMemo(
			() => ["10%", parentIndex === 1 ? "92%" : "88%"],
			[parentIndex],
		);
		const lastScrollPosition = useRef(0);
		const shouldScrollToHighlight = useRef(false);

		const theme = useAppTheme();
		const bottomSheetModalRef = useRef<BottomSheetModal>(null);
		const hasBothParentAudioAndStem =
			slideshowData && slideshowData.length > 0 && parentStem;

		const bottomSheetScrollViewRef = useRef<ScrollView>(null);
		const highlightedTextRef = useRef<TextRef>(null);

		useImperativeHandle(ref, () => ({
			scrollToHighlightedText,
		}));

		useEffect(() => {
			if (showModal) {
				bottomSheetModalRef.current?.present();
			}
		}, [showModal]);

		const scrollToHighlightedText = useCallback(() => {
			shouldScrollToHighlight.current = true;
			const highlightedText = highlightedTextRef.current;
			if (highlightedText) {
				highlightedText.measureLayout(
					bottomSheetScrollViewRef.current?.getScrollableNode(),
					(_, y) => {
						bottomSheetScrollViewRef.current?.scrollTo({
							y,
							animated: true,
						});
						lastScrollPosition.current = y;
					},
				);
			}
		}, []);

		const handleSheetChanges = useCallback(
			(index: number) => {
				if (index === -1) {
					toggleShowModal();
					lastScrollPosition.current = 0;
					shouldScrollToHighlight.current = false;
					return;
				}

				if (index === 1 && shouldScrollToHighlight.current) {
					scrollToHighlightedText();
				}
			},
			[toggleShowModal, scrollToHighlightedText],
		);

		const handleCollapse = () => {
			bottomSheetModalRef.current?.close();
		};

		const ReadView = () => {
			return (
				<Box gap={16}>
					<Text variant="titleLarge">{parentTitle}</Text>
					{insertText && (
						<Box gap={16}>
							<Divider />
							<InfoAlert icon="text" text={insertText} />
							<Divider />
						</Box>
					)}
					{parentStem && (
						<ItemStem
							text={parentStem}
							fontSize={14}
							handleClickInsertableText={handleClickInsertableText}
							// biome-ignore lint/suspicious/noExplicitAny: ref typing override
							highlightedTextRef={highlightedTextRef as any}
							disableInsertableText={disableInsertableText}
							customMarkdownStyles={{
								paragraph: {
									marginVertical: 10,
								},
							}}
						/>
					)}
					{parentVideo && <VideoPlayer videoUrl={parentVideo} />}
					{parentAudio && (
						<AudioPlayerContainer uri={parentAudio} itemId={itemId} />
					)}
					{!hasBothParentAudioAndStem && (
						<SlideShowRenderer slideshowData={slideshowData} itemId={itemId} />
					)}
				</Box>
			);
		};

		const NonTabbedbView = () => {
			return (
				<ScrollView
					ref={bottomSheetScrollViewRef}
					contentContainerStyle={{
						padding: 16,
						paddingBottom: 100,
						gap: 16,
					}}
					onScroll={onScroll}
					scrollEventThrottle={20}
					contentOffset={{ x: 0, y: lastScrollPosition.current }}
				>
					{parentInstruction && parentIndex === 1 && (
						<InfoAlert text={parentInstruction} />
					)}
					{professor ? (
						<DiscussionView professor={professor} title={parentTitle!} />
					) : (
						<ReadView />
					)}
				</ScrollView>
			);
		};

		const handleCloseModal = () => {
			handleCollapse();
		};

		const Header = () => {
			return (
				<Box paddingBottom={16}>
					<SheetHandle onPress={handleCloseModal} />
					<Box
						flexDirection="row"
						justifyContent="space-between"
						alignItems="center"
						paddingHorizontal={16}
					>
						{skill && <StimulusInfo skill={skill} />}
						<Button mode="contained" onPress={handleCloseModal}>
							{t("common.continue")}
						</Button>
					</Box>
				</Box>
			);
		};

		// if (mode === "modal") {
		// 	return (
		// 		<Box paddingTop={isAndroid ? statusBarHeight : 0} flex={1}>
		// 			<Header />
		// 			{isSlidesHasPassage && slides && slides.length > 0 ? (
		// 				<StimulusTabContent
		// 					parentTitle={parentTitle!}
		// 					slideshowData={slideshowData!}
		// 					parentInstruction={parentInstruction!}
		// 					parentIndex={parentIndex!}
		// 					slides={slides!}
		// 				/>
		// 			) : (
		// 				<NonTabbedbView />
		// 			)}
		// 		</Box>
		// 	);
		// }

		const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
			lastScrollPosition.current = event.nativeEvent.contentOffset.y;
			shouldScrollToHighlight.current = false;
		};

		return (
			<BottomSheetModalProvider>
				<BottomSheetModal
					ref={bottomSheetModalRef}
					enablePanDownToClose
					enableContentPanningGesture={false}
					index={1}
					style={{
						shadowColor: theme.colors.shadow,
						shadowOffset: {
							width: 0,
							height: 0,
						},
						shadowOpacity: 1,
						shadowRadius: 8,
						elevation: 8,
					}}
					backgroundStyle={{
						backgroundColor: theme.colors.elevation.level1,
					}}
					animateOnMount={mode === "sheet"}
					snapPoints={snapPoints}
					onChange={handleSheetChanges}
					backdropComponent={BottomSheetBackdrop}
					handleComponent={() => <Header />}
				>
					{isSlidesHasPassage && slides && slides.length > 0 ? (
						<StimulusTabContent
							parentTitle={parentTitle!}
							slideshowData={slideshowData!}
							parentInstruction={parentInstruction!}
							parentIndex={parentIndex!}
							slides={slides!}
							itemId={itemId}
						/>
					) : (
						<NonTabbedbView />
					)}
				</BottomSheetModal>
			</BottomSheetModalProvider>
		);
	},
);

export default PassageStemModal;
